import React, { useState, useEffect, useCallback } from 'react';
import axios, { API } from '../lib/api';
import { getCategoryMeta } from './FeedbackButton';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminFeedbackTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const FEEDBACK_CATEGORIES = [
    { value: 'bug', label: 'Bug / Issue' },
    { value: 'suggestion', label: 'Suggestion' },
    { value: 'other', label: 'Other' },
  ];

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/feedback/admin`);
      setItems(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/feedback/admin/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success('Feedback deleted');
    } catch {
      toast.error('Failed to delete feedback');
    }
  };

  const filtered = filter === 'all' ? items : items.filter((i) => i.category === filter);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-justb-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading feedback...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Feedback Inbox</h2>
          <p className="text-sm text-gray-500">{items.length} total submission{items.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-justb-primary hover:bg-justb-primary/90' : ''}
          >
            All ({items.length})
          </Button>
          {FEEDBACK_CATEGORIES.map((cat) => {
            const count = items.filter((i) => i.category === cat.value).length;
            return (
              <Button
                key={cat.value}
                variant={filter === cat.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(cat.value)}
                className={filter === cat.value ? 'bg-justb-primary hover:bg-justb-primary/90' : ''}
              >
                {cat.label} ({count})
              </Button>
            );
          })}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">
              {items.length === 0 ? 'No feedback submissions yet.' : 'No feedback in this category.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => {
            const meta = getCategoryMeta(item.category);
            const Icon = meta.icon;
            const dateStr = new Date(item.created_date).toLocaleString();
            return (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Icon className={`h-3 w-3 ${meta.color}`} />
                          {meta.label}
                        </Badge>
                        <span className="text-sm text-gray-500">from</span>
                        <span className="text-sm font-medium text-gray-900">{item.username}</span>
                        {item.contact_email && (
                          <a href={`mailto:${item.contact_email}?subject=Re: Your Just B feedback`} className="text-sm text-justb-secondary hover:underline">
                            ({item.contact_email})
                          </a>
                        )}
                        <span className="text-xs text-gray-400">&middot; {dateStr}</span>
                      </div>
                      <p className="text-gray-800 whitespace-pre-wrap break-words">{item.message}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      aria-label="Delete feedback"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminFeedbackTab;
